import { z } from 'zod';

import { jsonRequest, resolveApiFetch } from './shared';
import { assertOk } from './response';
import { loadOfflineSnapshot, saveOfflineSnapshot } from '@/lib/offline/dataStore';
import { OFFLINE_TABLES_VERSION } from '@/lib/offline/constants';

export interface RestaurantTable {
  id: string;
  name?: string | null;
  tableNumber?: number | string;
  location?: string | null;
  locationId?: string | null;
  seats?: number | null;
  qrCodeSlug: string;
  zoneId?: string | null;
  zone?: string | null;
  zoneColor?: string | null;
  status?: 'OPEN' | 'OCCUPIED' | 'RESERVED';
  lastOrderAt?: string | null;
  deepLink: string;
  deepLinkToken?: string | null;
  deepLinkExpiresAt?: string | null;
  qrPngDataUrl: string;
  displayOrder?: number;
  tamperCount?: number;
  lastTamperAt?: string | null;
  hasPendingCash?: boolean;
  pendingCashOrderCount?: number;
  pendingCashTotalCents?: number;
  pendingCashOrders?: Array<{
    orderId: string;
    orderCode?: string | null;
    status?: string | null;
    paymentStatus?: string | null;
    checkoutPaymentMethod?: string | null;
    outstandingCashCents: number;
    totalCents: number;
    placedAt?: string | null;
    updatedAt?: string | null;
    pendingSince?: string | null;
    pendingPaymentCount?: number;
  }>;
}

type ServerTable = {
  id: string;
  name?: string | null;
  table_number?: number | string;
  qr_slug?: string;
  qrCodeSlug?: string;
  slug?: string;
  link?: string;
  deepLink?: string;
  qr_data_url?: string;
  qrPngDataUrl?: string;
  tableNumber?: number | string;
  location?: string | null;
  locationId?: string | null;
  seats?: number | null;
  status?: 'OPEN' | 'OCCUPIED' | 'RESERVED';
  lastOrderAt?: string | null;
  deepLinkToken?: string | null;
  deep_link_token?: string | null;
  token?: string | null;
  deepLinkExpiresAt?: string | null;
  deep_link_expires_at?: string | null;
  expires_at?: string | null;
  zoneId?: string | null;
  zone_id?: string | null;
  zone?: string | null;
  zoneName?: string | null;
  zone_name?: string | null;
  zoneColor?: string | null;
  zone_color?: string | null;
  tamperCount?: number;
  tamper_count?: number;
  lastTamperAt?: string | number | null;
  last_tamper_at?: string | number | null;
  hasPendingCash?: boolean;
  has_pending_cash?: boolean;
  pendingCashOrderCount?: number;
  pending_cash_order_count?: number;
  pendingCashTotalCents?: number;
  pending_cash_total_cents?: number;
  pendingCashOrders?: Array<ServerPendingCashOrder>;
  pending_cash_orders?: Array<ServerPendingCashOrder>;
};

type ServerPendingCashOrder = {
  orderId?: string;
  order_id?: string;
  orderCode?: string | null;
  order_code?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  payment_status?: string | null;
  checkoutPaymentMethod?: string | null;
  checkout_payment_method?: string | null;
  outstandingCashCents?: number | null;
  outstanding_cash_cents?: number | null;
  totalCents?: number | null;
  total_cents?: number | null;
  placedAt?: string | null;
  placed_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
  pendingSince?: string | null;
  pending_since?: string | null;
  pendingPaymentCount?: number | null;
  pending_payment_count?: number | null;
};

type ServerQrAssets = {
  pngDataUrl?: string;
  svg?: string;
  deepLink?: string;
} | null | undefined;

const resolveDeepLink = (t: ServerTable, assets: ServerQrAssets): string => {
  const assetLink = assets?.deepLink;
  return t.deepLink ?? t.link ?? assetLink ?? '';
};

const resolveQrPng = (t: ServerTable, assets: ServerQrAssets): string => {
  const assetPng = assets?.pngDataUrl;
  return t.qrPngDataUrl ?? t.qr_data_url ?? assetPng ?? '';
};

const coerceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const coerceBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return null;
};

const coerceString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }
  return null;
};

const normalizePendingCashOrders = (
  orders?: Array<ServerPendingCashOrder | null | undefined>,
): RestaurantTable['pendingCashOrders'] => {
  if (!Array.isArray(orders) || orders.length === 0) {
    return undefined;
  }

  const normalised = orders
    .map((raw) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }
      const record = raw as ServerPendingCashOrder;
      const orderId = coerceString(record.orderId ?? record.order_id);
      if (!orderId) {
        return null;
      }
      const outstanding =
        coerceNumber(record.outstandingCashCents ?? record.outstanding_cash_cents) ?? 0;
      const totalCents = coerceNumber(record.totalCents ?? record.total_cents) ?? 0;
      const pendingSince = coerceString(record.pendingSince ?? record.pending_since);

      return {
        orderId,
        orderCode: coerceString(record.orderCode ?? record.order_code),
        status: coerceString(record.status),
        paymentStatus: coerceString(record.paymentStatus ?? record.payment_status),
        checkoutPaymentMethod: coerceString(
          record.checkoutPaymentMethod ?? record.checkout_payment_method,
        ),
        outstandingCashCents: Math.max(0, Math.round(outstanding)),
        totalCents: Math.max(0, Math.round(totalCents)),
        placedAt: coerceString(record.placedAt ?? record.placed_at),
        updatedAt: coerceString(record.updatedAt ?? record.updated_at),
        pendingSince: pendingSince ?? null,
        pendingPaymentCount:
          coerceNumber(record.pendingPaymentCount ?? record.pending_payment_count) ?? 0,
      };
    })
    .filter(
      (entry): entry is NonNullable<RestaurantTable['pendingCashOrders']>[number] => Boolean(entry),
    );

  return normalised.length > 0 ? normalised : undefined;
};

function mapToRestaurantTable(t: ServerTable, assets?: ServerQrAssets): RestaurantTable {
  const tableNumber = t.tableNumber ?? t.table_number;
  const slug = t.qrCodeSlug ?? t.qr_slug ?? t.slug ?? '';
  const deepLink = resolveDeepLink(t, assets);
  const qrPngDataUrl = resolveQrPng(t, assets);
  const deepLinkToken = t.deepLinkToken ?? t.deep_link_token ?? t.token ?? null;
  const deepLinkExpiresAt =
    t.deepLinkExpiresAt ?? t.deep_link_expires_at ?? t.expires_at ?? null;
  const locationId = t.locationId ?? (t as { location_id?: string | null }).location_id ?? null;
  const zoneId = t.zoneId ?? t.zone_id ?? null;
  const zoneName = t.zone ?? t.zoneName ?? t.zone_name ?? null;
  const zoneColor = t.zoneColor ?? t.zone_color ?? null;
  const displayOrder = (t as { display_order?: number; displayOrder?: number }).displayOrder ?? (t as { display_order?: number }).display_order ?? null;
  const tamperCountValue =
    (t as { tamperCount?: number }).tamperCount ??
    (t as { tamper_count?: number }).tamper_count ??
    0;
  const tamperCountNumeric = Number(tamperCountValue ?? 0);
  const lastTamperRaw =
    (t as { lastTamperAt?: string | number | null }).lastTamperAt ??
    (t as { last_tamper_at?: string | number | null }).last_tamper_at ??
    null;
  const lastTamperAt =
    typeof lastTamperRaw === 'number' && Number.isFinite(lastTamperRaw)
      ? new Date(lastTamperRaw).toISOString()
      : typeof lastTamperRaw === 'string'
        ? lastTamperRaw
        : null;

  const resolvedPendingOrders = normalizePendingCashOrders(
    (t.pendingCashOrders ?? t.pending_cash_orders) as
      | Array<ServerPendingCashOrder | null | undefined>
      | undefined,
  );
  const resolvedHasPendingCash =
    coerceBoolean((t as { hasPendingCash?: boolean }).hasPendingCash) ??
    coerceBoolean((t as { has_pending_cash?: boolean }).has_pending_cash) ??
    Boolean(resolvedPendingOrders && resolvedPendingOrders.length > 0);
  const resolvedPendingTotal =
    coerceNumber((t as { pendingCashTotalCents?: number }).pendingCashTotalCents) ??
    coerceNumber((t as { pending_cash_total_cents?: number }).pending_cash_total_cents) ??
    (resolvedPendingOrders
      ? resolvedPendingOrders.reduce((sum, entry) => sum + entry.outstandingCashCents, 0)
      : 0);
  const resolvedPendingCount =
    coerceNumber((t as { pendingCashOrderCount?: number }).pendingCashOrderCount) ??
    coerceNumber((t as { pending_cash_order_count?: number }).pending_cash_order_count) ??
    (resolvedPendingOrders ? resolvedPendingOrders.length : 0);

  return {
    id: t.id,
    name: t.name ?? null,
    tableNumber: tableNumber ?? undefined,
    location: t.location ?? null,
    locationId: locationId ?? undefined,
    seats: t.seats ?? null,
    status: t.status,
    lastOrderAt: t.lastOrderAt ?? null,
    qrCodeSlug: slug,
    deepLink,
    deepLinkToken: deepLinkToken ?? undefined,
    deepLinkExpiresAt: deepLinkExpiresAt ?? undefined,
    zoneId: zoneId ?? undefined,
    zone: zoneName ?? undefined,
    zoneColor: zoneColor ?? undefined,
    displayOrder: displayOrder ?? undefined,
    qrPngDataUrl,
    tamperCount: Number.isFinite(tamperCountNumeric) ? tamperCountNumeric : 0,
    lastTamperAt: lastTamperAt ?? null,
    hasPendingCash: resolvedHasPendingCash,
    pendingCashOrderCount: Math.max(0, Math.round(resolvedPendingCount ?? 0)),
    pendingCashTotalCents: Math.max(0, Math.round(resolvedPendingTotal ?? 0)),
    pendingCashOrders: resolvedPendingOrders ?? [],
  };
}

export interface FetchTablesResponse {
  tables: RestaurantTable[];
  cacheStatus?: 'network' | 'cache';
  cachedAt?: number;
}

export interface TableActivityHistoryPoint {
  date: string;
  activeCount: number;
}

export interface TableActivitySummaryResponse {
  totals: {
    total: number;
    active: number;
    reserved: number;
    available: number;
  };
  activeTableIds: string[];
  reservedTableIds: string[];
  history: TableActivityHistoryPoint[];
  recencyHours: number;
}

export interface ListTablesFilters {
  search?: string;
  locationId?: string | null;
  zoneId?: string | null;
  color?: string | null;
  tamper?: string | null;
}

export interface ListTablesOptions {
  signal?: AbortSignal;
  filters?: ListTablesFilters;
}

export async function listTables(tenantSlug: string, options?: ListTablesOptions): Promise<FetchTablesResponse> {
  const params = new URLSearchParams();
  const filters = options?.filters;
  if (filters?.search) {
    params.set('search', filters.search);
  }
  if (filters?.locationId) {
    params.set('locationId', filters.locationId);
  }
  if (filters?.zoneId) {
    params.set('zoneId', filters.zoneId);
  }
  if (filters?.color) {
    params.set('color', filters.color);
  }
  if (filters?.tamper) {
    params.set('tamper', filters.tamper);
  }
  if (filters?.color) {
    params.set('color', filters.color);
  }
  if (filters?.tamper) {
    params.set('tamper', filters.tamper);
  }

  const path = `/tables${params.size ? `?${params.toString()}` : ''}`;

  try {
    const apiFetch = await resolveApiFetch();
    const r = await apiFetch(path, {
      signal: options?.signal,
      headers: { 'X-Tenant': tenantSlug },
    });
    if (r.status === 404) {
      const cachedAt = Date.now();
      const result: FetchTablesResponse = { tables: [] };
      saveOfflineSnapshot('tables', tenantSlug, { ...result, cachedAt }, OFFLINE_TABLES_VERSION);
      return { ...result, cacheStatus: 'network', cachedAt };
    }
    await assertOk(r);
    const data = await r.json();
    const tables = Array.isArray(data?.tables) ? data.tables : [];
    const result: FetchTablesResponse = { tables: tables.map(mapToRestaurantTable) };
    const cachedAt = Date.now();
    if (!filters || Object.keys(filters).length === 0) {
      saveOfflineSnapshot('tables', tenantSlug, { ...result, cachedAt }, OFFLINE_TABLES_VERSION);
    }
    return { ...result, cacheStatus: 'network', cachedAt };
  } catch (error) {
    const fallback = !filters || Object.keys(filters).length === 0
      ? loadOfflineSnapshot<FetchTablesResponse>('tables', tenantSlug, OFFLINE_TABLES_VERSION)
      : null;
    if (fallback) {
      return { ...fallback.data, cacheStatus: 'cache', cachedAt: fallback.cachedAt };
    }
    throw error;
  }
}

export async function exportTablesCsv(tenantSlug: string, filters?: ListTablesFilters): Promise<Blob> {
  const params = new URLSearchParams();
  if (filters?.search) {
    params.set('search', filters.search);
  }
  if (filters?.locationId) {
    params.set('locationId', filters.locationId);
  }
  if (filters?.zoneId) {
    params.set('zoneId', filters.zoneId);
  }
  if (filters?.color) {
    params.set('color', filters.color);
  }
  if (filters?.tamper) {
    params.set('tamper', filters.tamper);
  }

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/tables/export${params.size ? `?${params.toString()}` : ''}`, {
    headers: { Accept: 'text/csv', 'X-Tenant': tenantSlug },
  });

  await assertOk(response);

  const data = await response.arrayBuffer();
  return new Blob([data], { type: 'text/csv' });
}

// Backwards aliases
export const getTables = listTables;
export const fetchTables = listTables;

export async function fetchTableActivitySummary(
  tenantSlug: string,
  signal?: AbortSignal,
): Promise<TableActivitySummaryResponse> {
  const payload = (await jsonRequest<Record<string, unknown>>('/dashboard/table-activity', {
    signal,
    headers: { 'X-Tenant': tenantSlug },
    schema: z.record(z.unknown()),
  })) as Partial<{
    totals: Partial<TableActivitySummaryResponse['totals']> | null;
    activeTableIds: unknown;
    reservedTableIds: unknown;
    history: Array<Partial<TableActivityHistoryPoint> | null> | null;
    recencyHours: unknown;
  }>;

  const normalizeIds = (input: unknown): string[] =>
    Array.isArray(input) ? input.map((value) => (typeof value === 'string' ? value : String(value))).filter(Boolean) : [];

  const normalizeHistory = (input: unknown): TableActivityHistoryPoint[] => {
    if (!Array.isArray(input)) {
      return [];
    }

    return input.map((raw) => {
      const entry = (raw ?? {}) as {
        date?: unknown;
        activeCount?: unknown;
        active_count?: unknown;
      };

      const date = typeof entry.date === 'string' ? entry.date : '';

      let countCandidate: unknown = entry.activeCount;
      if (!Number.isFinite(countCandidate as number)) {
        countCandidate = entry.active_count;
      }

      const parsed =
        typeof countCandidate === 'number' && Number.isFinite(countCandidate)
          ? countCandidate
          : Number(countCandidate ?? 0);

      return {
        date,
        activeCount: Number.isFinite(parsed) ? Number(parsed) : 0,
      };
    });
  };

  const rawTotals = payload.totals ?? {};
  const totals = {
    total: Number(rawTotals.total ?? 0) || 0,
    active: Number(rawTotals.active ?? 0) || 0,
    reserved: Number(rawTotals.reserved ?? 0) || 0,
    available: Number(rawTotals.available ?? 0) || 0,
  };

  return {
    totals,
    activeTableIds: normalizeIds(payload.activeTableIds),
    reservedTableIds: normalizeIds(payload.reservedTableIds),
    history: normalizeHistory(payload.history),
    recencyHours:
      typeof payload.recencyHours === 'number' && Number.isFinite(payload.recencyHours) && payload.recencyHours > 0
        ? payload.recencyHours
        : 4,
  };
}

export interface CreateTableOptions {
  name?: string;
  tableNumber?: number;
  locationId?: string;
  qrCodeSlug?: string;
  zoneId?: string;
}

const serialiseCreateTablePayload = (options?: CreateTableOptions | string): Record<string, unknown> => {
  if (!options) {
    return {};
  }

  if (typeof options === 'string') {
    const trimmed = options.trim();
    return trimmed.length > 0 ? { name: trimmed } : {};
  }

  const payload: Record<string, unknown> = {};

  if (options.name && options.name.trim().length > 0) {
    payload.name = options.name.trim();
  }

  if (typeof options.tableNumber === 'number' && Number.isFinite(options.tableNumber)) {
    payload.tableNumber = options.tableNumber;
  }

  if (options.locationId && options.locationId.trim().length > 0) {
    payload.locationId = options.locationId.trim();
  }

  if (options.zoneId && options.zoneId.trim().length > 0) {
    payload.zoneId = options.zoneId.trim();
  }

  if (options.qrCodeSlug && options.qrCodeSlug.trim().length > 0) {
    payload.qrCodeSlug = options.qrCodeSlug.trim();
  }

  return payload;
};

export async function createTable(tenantSlug: string, options?: CreateTableOptions | string): Promise<RestaurantTable> {
  const payload = serialiseCreateTablePayload(options);

  const response = await jsonRequest<unknown>('/tables', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantSlug },
    body: payload,
    schema: z.unknown(),
  });
  const table = ensureServerTable(response);
  const assets = extractAssets(response);
  return mapToRestaurantTable(table, assets);
}

export interface RegeneratedTableAssets {
  table: RestaurantTable;
  assets: {
    pngDataUrl: string;
    deepLink: string;
    svg?: string;
  };
}

const ensureServerTable = (payload: unknown): ServerTable => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid server response (missing table payload)');
  }

  if ('table' in payload && payload.table && typeof payload.table === 'object') {
    return payload.table as ServerTable;
  }

  if ('id' in payload) {
    return payload as ServerTable;
  }

  throw new Error('Invalid server response (table payload missing id)');
};

const extractAssets = (payload: unknown): ServerQrAssets => {
  if (payload && typeof payload === 'object' && 'assets' in payload) {
    const assets = (payload as { assets?: ServerQrAssets }).assets;
    if (assets && typeof assets === 'object') {
      return assets;
    }
  }
  return null;
};

export interface UpdateTableOptions {
  tableNumber?: number;
  locationId?: string | null;
  qrCodeSlug?: string;
  zoneId?: string | null;
}

const serialiseUpdateTablePayload = (options: UpdateTableOptions): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (options.tableNumber !== undefined) {
    payload.tableNumber = options.tableNumber;
  }

  if (options.locationId !== undefined) {
    payload.locationId = options.locationId;
  }

  if (options.qrCodeSlug !== undefined) {
    const trimmed = options.qrCodeSlug?.trim();
    if (trimmed && trimmed.length > 0) {
      payload.qrCodeSlug = trimmed;
    }
  }

  if (options.zoneId !== undefined) {
    payload.zoneId = options.zoneId;
  }

  return payload;
};

export async function updateTable(
  tenantSlug: string,
  id: string,
  options: UpdateTableOptions,
): Promise<RestaurantTable> {
  const payload = serialiseUpdateTablePayload(options);

  const response = await jsonRequest<unknown>(`/tables/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Tenant': tenantSlug },
    body: payload,
    schema: z.unknown(),
  });
  const table = ensureServerTable(response?.table ?? response);
  const assets = extractAssets(response);
  return mapToRestaurantTable(table, assets);
}

export interface ReleaseTableResponse {
  ok: boolean;
  completedOrders: number;
}

export async function deleteTable(tenantSlug: string, id: string): Promise<void> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/tables/${id}`, {
    method: 'DELETE',
    headers: { 'X-Tenant': tenantSlug },
  });

  await assertOk(response);
}

export async function releaseTable(tenantSlug: string, id: string): Promise<ReleaseTableResponse> {
  return jsonRequest<ReleaseTableResponse>(`/tables/${id}/release`, {
    method: 'POST',
    headers: { 'X-Tenant': tenantSlug },
    schema: z
      .object({
      ok: z.boolean(),
      completedOrders: z.number(),
    })
      .passthrough(),
  });
}

export async function regenTableQr(tenantSlug: string, id: string): Promise<RegeneratedTableAssets> {
  const payload = await jsonRequest<unknown>(`/tables/${id}/qr`, {
    method: 'POST',
    headers: { 'X-Tenant': tenantSlug },
    schema: z.unknown(),
  });
  const assets = extractAssets(payload);
  const rawTable = ensureServerTable(payload);

  const normalizedTable = mapToRestaurantTable(
    {
      ...rawTable,
      deepLink: resolveDeepLink(rawTable, assets),
      qrPngDataUrl: resolveQrPng(rawTable, assets),
    },
    assets,
  );

  return {
    table: normalizedTable,
    assets: {
      pngDataUrl: normalizedTable.qrPngDataUrl,
      deepLink: normalizedTable.deepLink,
      svg: assets?.svg,
    },
  };
}
